import { Alert, SelectedSource } from '@aloft/toolkit-shared';

import { get } from 'lodash';

import { SubjectPropertyDataState } from '../../../../redux/types.ts';
import { AlertDefinition } from '../../types.ts';
import { getAlertTriggers } from '../utils/alertTriggers.ts';

// Flat keys map directly to `improvements.<key>`.
// Dotted keys (e.g. 'basement.fullBaths') are sub-feature fields — the prefix ('basement')
// must have `hasFeature: true` on the subject before the field is considered relevant.
const IMPROVEMENT_FIELDS: Record<string, string> = {
  bedrooms: 'Bedrooms',
  fullBaths: 'Full Baths',
  halfBaths: 'Half Baths',
  'basement.fullBaths': 'Basement Full Baths',
  'basement.halfBaths': 'Basement Half Baths',
  'adu.fullBaths': 'ADU Full Baths',
  'adu.halfBaths': 'ADU Half Baths',
};

export const inconsistentSubjectBedBathCount: AlertDefinition = ({
  propertyDataEntities,
  conflictedFields,
  order,
}) => {
  const triggers = getAlertTriggers({ fixRoute: 'features', order });
  const subjectEntity = propertyDataEntities.subject as SubjectPropertyDataState;
  const improvements = subjectEntity.subjectData.editableData?.improvements;
  const selectedSourceImprovements =
    subjectEntity.subjectData.selectedSources?.editableData?.improvements;

  return Object.entries(IMPROVEMENT_FIELDS)
    .map(([fieldKey, fieldName]) => {
      const subFeatureParent = fieldKey.includes('.') ? fieldKey.split('.')[0] : undefined;

      if (subFeatureParent && !get(improvements, `${subFeatureParent}.hasFeature`)) {
        return undefined;
      }

      const fieldPath = `editableData.improvements.${fieldKey}` as const;

      const foundConflict = Object.values(conflictedFields).find(
        (conflict) => conflict.fieldName === fieldPath && conflict.propertyId === 'subject',
      );

      if (!foundConflict) return undefined;

      const selectedSource = get(selectedSourceImprovements, fieldKey) as
        | SelectedSource
        | undefined;
      const conflictIsResolved = selectedSource?.conflictIsResolved;

      const fixingInfo = {
        tips: '',
        fixingMethod: ['action', 'comment'],
        commentAlwaysRequired: true,
        targetCellKey: `subject-${fieldPath}`,
        pairedCostRouteItem: 'features',
      } as Alert['fixingInfo'];

      if (!conflictIsResolved) {
        return {
          type: 'inconsistentSubjectBedBathCount',
          message: `Subject's ${fieldName} has conflicting values between data sources.`,
          severity: 'error',
          status: 'active',
          orderType: ['appraisify'],
          triggers,
          propertyID: 'subject',
          fixingInfo: {
            ...fixingInfo,
            tips: 'Review all sources and reconcile the conflicts, or provide an explanation for the selected values.',
          },
        } as Alert;
      }

      return {
        type: 'inconsistentSubjectBedBathCount',
        message: `Subject's ${fieldName} has conflicting values between data sources.`,
        severity: 'error',
        status: 'resolved_pending_comment',
        orderType: ['appraisify'],
        propertyID: 'subject',
        triggers,
        fixingInfo: {
          ...fixingInfo,
          tips: `Please provide detailed commentary explaining the rationale behind the selected value for ${fieldName}.`,
        },
      } as Alert;
    })
    .filter((alert): alert is Alert => alert !== undefined);
};
